import { Add, Article, Delete, Description, Edit } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import Layout from '../components/Layout';
import StoragePickerDialog from '../components/StoragePickerDialog';

const CmsPage = () => {
  const [tab, setTab] = useState(0);
  const [posts, setPosts] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [postDialog, setPostDialog] = useState({ open: false, editing: null });
  const [pageDialog, setPageDialog] = useState({ open: false, editing: null });
  const [picker, setPicker] = useState({ open: false, for: null, index: -1, target: 'post' });
  const [postForm, setPostForm] = useState({ title: '', slug: '', content: '', status: 'draft', excerpt: '', components: [] });
  const [pageForm, setPageForm] = useState({ title: '', slug: '', content: '', status: 'draft', components: [] });

  const loadData = async () => {
    try {
      setLoading(true);
      const [postRes, pageRes] = await Promise.all([
        apiClient.get('/cms/posts'),
        apiClient.get('/cms/pages')
      ]);
      setPosts(postRes.data?.data || postRes.data || []);
      setPages(pageRes.data?.data || pageRes.data || []);
      setError('');
    } catch (e) {
      setError('Не удалось загрузить данные CMS');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Реалтайм-обновления через SSE
  useEffect(() => {
    let es;
    try {
      const base = apiClient.defaults?.baseURL || 'http://127.0.0.1:5000';
      es = new EventSource(`${base}/public/stream`);
      es.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data || '{}');
          if (msg.type && (msg.type.startsWith('cms.post') || msg.type.startsWith('cms.page'))) {
            loadData();
          }
        } catch (e) { }
      };
      es.onerror = () => { try { es.close(); } catch (e) { } };
    } catch (e) { }
    return () => { try { es && es.close(); } catch (e) { } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPost = (p = null) => {
    setPostDialog({ open: true, editing: p });
    setPostForm(p ? { title: p.title || '', slug: p.slug || '', content: p.content || '', status: p.status || 'draft', excerpt: p.excerpt || '', publishedAt: p.publishedAt || '', components: Array.isArray(p.components) ? p.components : [] } : { title: '', slug: '', content: '', status: 'draft', excerpt: '', components: [] });
  };
  const openPage = (p = null) => {
    setPageDialog({ open: true, editing: p });
    setPageForm(p ? { title: p.title || '', slug: p.slug || '', content: p.content || '', status: p.status || 'draft', seoTitle: p.seoTitle || '', seoDescription: p.seoDescription || '', components: Array.isArray(p.components) ? p.components : [] } : { title: '', slug: '', content: '', status: 'draft', components: [] });
  };

  const savePost = async () => {
    try {
      if (postDialog.editing) {
        await apiClient.put(`/cms/posts/${postDialog.editing._id || postDialog.editing.id}`, postForm);
      } else {
        await apiClient.post('/cms/posts', postForm);
      }
      setPostDialog({ open: false, editing: null });
      loadData();
    } catch (e) {
      setError('Не удалось сохранить пост');
    }
  };
  const savePage = async () => {
    try {
      if (pageDialog.editing) {
        await apiClient.put(`/cms/pages/${pageDialog.editing._id || pageDialog.editing.id}`, pageForm);
      } else {
        await apiClient.post('/cms/pages', pageForm);
      }
      setPageDialog({ open: false, editing: null });
      loadData();
    } catch (e) {
      setError('Не удалось сохранить страницу');
    }
  };

  const delPost = async (p) => {
    if (!window.confirm('Удалить пост?')) return;
    try {
      await apiClient.delete(`/cms/posts/${p._id || p.id}`);
      loadData();
    } catch (e) { setError('Не удалось удалить пост'); }
  };
  const delPage = async (p) => {
    if (!window.confirm('Удалить страницу?')) return;
    try {
      await apiClient.delete(`/cms/pages/${p._id || p.id}`);
      loadData();
    } catch (e) { setError('Не удалось удалить страницу'); }
  };

  // ===== Конструктор блоков (text, image, button) =====
  const addPostComponent = (type) => {
    const next = [...(postForm.components || [])];
    if (type === 'text') next.push({ type: 'text', props: { content: '' } });
    if (type === 'image') next.push({ type: 'image', props: { src: '', alt: '' } });
    if (type === 'button') next.push({ type: 'button', props: { label: '', url: '' } });
    setPostForm({ ...postForm, components: next });
  };
  const updatePostComponent = (idx, key, value) => {
    const next = [...(postForm.components || [])];
    next[idx] = { ...next[idx], props: { ...(next[idx]?.props || {}), [key]: value } };
    setPostForm({ ...postForm, components: next });
  };
  const removePostComponent = (idx) => {
    const next = [...(postForm.components || [])];
    next.splice(idx, 1);
    setPostForm({ ...postForm, components: next });
  };
  const selectPostImageFromCloud = (idx) => {
    setPicker({ open: true, for: 'image', index: idx, target: 'post' });
  };
  const onPickDone = async (key) => {
    try {
      const base = apiClient.defaults?.baseURL || '';
      // Получим публичный presigned URL
      const publicKey = localStorage.getItem('org_public_key');
      if (!publicKey) return;
      const resp = await fetch(`${base}/public/storage/files/${encodeURIComponent(key)}/url?public_key=${encodeURIComponent(publicKey)}`);
      const data = await resp.json();
      const url = data?.url || '';
      if (picker.target === 'post') {
        const next = [...(postForm.components || [])];
        if (next[picker.index]?.type === 'image') {
          next[picker.index] = { ...next[picker.index], props: { ...(next[picker.index]?.props || {}), src: url } };
          setPostForm({ ...postForm, components: next });
        }
      } else {
        const next = [...(pageForm.components || [])];
        if (next[picker.index]?.type === 'image') {
          next[picker.index] = { ...next[picker.index], props: { ...(next[picker.index]?.props || {}), src: url } };
          setPageForm({ ...pageForm, components: next });
        }
      }
    } finally {
      setPicker({ open: false, for: null, index: -1, target: 'post' });
    }
  };
  const selectPageImageFromCloud = (idx) => {
    setPicker({ open: true, for: 'image', index: idx, target: 'page' });
  };

  const addPageComponent = (type) => {
    const next = [...(pageForm.components || [])];
    if (type === 'text') next.push({ type: 'text', props: { content: '' } });
    if (type === 'image') next.push({ type: 'image', props: { src: '', alt: '' } });
    if (type === 'button') next.push({ type: 'button', props: { label: '', url: '' } });
    setPageForm({ ...pageForm, components: next });
  };
  const updatePageComponent = (idx, key, value) => {
    const next = [...(pageForm.components || [])];
    next[idx] = { ...next[idx], props: { ...(next[idx]?.props || {}), [key]: value } };
    setPageForm({ ...pageForm, components: next });
  };
  const removePageComponent = (idx) => {
    const next = [...(pageForm.components || [])];
    next.splice(idx, 1);
    setPageForm({ ...pageForm, components: next });
  };

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>CMS</Typography>
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)}>
            <Tab icon={<Article />} iconPosition="start" label="Посты" />
            <Tab icon={<Description />} iconPosition="start" label="Страницы" />
          </Tabs>
        </Paper>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>{error}</Paper>
        )}

        {tab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button variant="contained" startIcon={<Add />} onClick={() => openPost(null)}>Новый пост</Button>
            </Box>
            <Grid container spacing={2}>
              {posts.map((p) => (
                <Grid item xs={12} md={6} lg={4} key={p._id || p.id}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6">{p.title}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>/{p.slug}</Typography>
                    <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'pre-wrap' }}>{p.excerpt || (p.content || '').slice(0, 160)}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton onClick={() => openPost(p)}><Edit /></IconButton>
                      <IconButton onClick={() => delPost(p)} color="error"><Delete /></IconButton>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button variant="contained" startIcon={<Add />} onClick={() => openPage(null)}>Новая страница</Button>
            </Box>
            <Grid container spacing={2}>
              {pages.map((p) => (
                <Grid item xs={12} md={6} lg={4} key={p._id || p.id}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6">{p.title}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>/{p.slug}</Typography>
                    <Typography variant="body2" sx={{ flex: 1, whiteSpace: 'pre-wrap' }}>{(p.content || '').slice(0, 160)}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton onClick={() => openPage(p)}><Edit /></IconButton>
                      <IconButton onClick={() => delPage(p)} color="error"><Delete /></IconButton>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Dialog open={postDialog.open} onClose={() => setPostDialog({ open: false, editing: null })} maxWidth="md" fullWidth>
          <DialogTitle>{postDialog.editing ? 'Редактировать пост' : 'Новый пост'}</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Заголовок" sx={{ mt: 1 }} value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} />
            <TextField fullWidth label="Slug" sx={{ mt: 1 }} value={postForm.slug} onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })} />
            <TextField fullWidth multiline minRows={6} label="Контент" sx={{ mt: 1 }} value={postForm.content} onChange={(e) => setPostForm({ ...postForm, content: e.target.value })} />
            <TextField fullWidth label="Краткое описание" sx={{ mt: 1 }} value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Статус</InputLabel>
                  <Select value={postForm.status} label="Статус" onChange={(e) => setPostForm({ ...postForm, status: e.target.value })}>
                    <MenuItem value="draft">Черновик</MenuItem>
                    <MenuItem value="published">Опубликован</MenuItem>
                    <MenuItem value="archived">Архив</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth type="datetime-local" label="Опубликовать в" InputLabelProps={{ shrink: true }} value={postForm.publishedAt || ''} onChange={(e) => setPostForm({ ...postForm, publishedAt: e.target.value })} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Блоки</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Button size="small" variant="outlined" onClick={() => addPostComponent('text')}>+ Текст</Button>
                <Button size="small" variant="outlined" onClick={() => addPostComponent('image')}>+ Картинка</Button>
                <Button size="small" variant="outlined" onClick={() => addPostComponent('button')}>+ Кнопка</Button>
              </Box>
              {(postForm.components || []).map((c, idx) => (
                <Paper key={idx} sx={{ p: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">{c.type}</Typography>
                    <Button color="error" size="small" onClick={() => removePostComponent(idx)}><Delete fontSize="small" /> Удалить</Button>
                  </Box>
                  {c.type === 'text' && (
                    <TextField fullWidth multiline minRows={3} label="Текст" value={c.props?.content || ''} onChange={(e) => updatePostComponent(idx, 'content', e.target.value)} />
                  )}
                  {c.type === 'image' && (
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={8}><TextField fullWidth label="URL изображения (или выберите из облака)" value={c.props?.src || ''} onChange={(e) => updatePostComponent(idx, 'src', e.target.value)} /></Grid>
                      <Grid item xs={12} md={4}><Button fullWidth variant="outlined" onClick={() => selectPostImageFromCloud(idx)}>Выбрать из облака</Button></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="ALT" value={c.props?.alt || ''} onChange={(e) => updatePostComponent(idx, 'alt', e.target.value)} /></Grid>
                    </Grid>
                  )}
                  {c.type === 'button' && (
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={5}><TextField fullWidth label="Текст кнопки" value={c.props?.label || ''} onChange={(e) => updatePostComponent(idx, 'label', e.target.value)} /></Grid>
                      <Grid item xs={12} md={7}><TextField fullWidth label="Ссылка" value={c.props?.url || ''} onChange={(e) => updatePostComponent(idx, 'url', e.target.value)} /></Grid>
                    </Grid>
                  )}
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPostDialog({ open: false, editing: null })}>Отмена</Button>
            <Button variant="contained" onClick={savePost}>Сохранить</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={pageDialog.open} onClose={() => setPageDialog({ open: false, editing: null })} maxWidth="md" fullWidth>
          <DialogTitle>{pageDialog.editing ? 'Редактировать страницу' : 'Новая страница'}</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Заголовок" sx={{ mt: 1 }} value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} />
            <TextField fullWidth label="Slug" sx={{ mt: 1 }} value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value })} />
            <TextField fullWidth multiline minRows={6} label="Контент" sx={{ mt: 1 }} value={pageForm.content} onChange={(e) => setPageForm({ ...pageForm, content: e.target.value })} />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Статус</InputLabel>
                  <Select value={pageForm.status} label="Статус" onChange={(e) => setPageForm({ ...pageForm, status: e.target.value })}>
                    <MenuItem value="draft">Черновик</MenuItem>
                    <MenuItem value="published">Опубликована</MenuItem>
                    <MenuItem value="archived">Архив</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="SEO Title" value={pageForm.seoTitle || ''} onChange={(e) => setPageForm({ ...pageForm, seoTitle: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} label="SEO Description" value={pageForm.seoDescription || ''} onChange={(e) => setPageForm({ ...pageForm, seoDescription: e.target.value })} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Блоки</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Button size="small" variant="outlined" onClick={() => addPageComponent('text')}>+ Текст</Button>
                <Button size="small" variant="outlined" onClick={() => addPageComponent('image')}>+ Картинка</Button>
                <Button size="small" variant="outlined" onClick={() => addPageComponent('button')}>+ Кнопка</Button>
              </Box>
              {(pageForm.components || []).map((c, idx) => (
                <Paper key={idx} sx={{ p: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">{c.type}</Typography>
                    <Button color="error" size="small" onClick={() => removePageComponent(idx)}><Delete fontSize="small" /> Удалить</Button>
                  </Box>
                  {c.type === 'text' && (
                    <TextField fullWidth multiline minRows={3} label="Текст" value={c.props?.content || ''} onChange={(e) => updatePageComponent(idx, 'content', e.target.value)} />
                  )}
                  {c.type === 'image' && (
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={8}><TextField fullWidth label="URL изображения (или выберите из облака)" value={c.props?.src || ''} onChange={(e) => updatePageComponent(idx, 'src', e.target.value)} /></Grid>
                      <Grid item xs={12} md={4}><Button fullWidth variant="outlined" onClick={() => selectPageImageFromCloud(idx)}>Выбрать из облака</Button></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="ALT" value={c.props?.alt || ''} onChange={(e) => updatePageComponent(idx, 'alt', e.target.value)} /></Grid>
                    </Grid>
                  )}
                  {c.type === 'button' && (
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={5}><TextField fullWidth label="Текст кнопки" value={c.props?.label || ''} onChange={(e) => updatePageComponent(idx, 'label', e.target.value)} /></Grid>
                      <Grid item xs={12} md={7}><TextField fullWidth label="Ссылка" value={c.props?.url || ''} onChange={(e) => updatePageComponent(idx, 'url', e.target.value)} /></Grid>
                    </Grid>
                  )}
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPageDialog({ open: false, editing: null })}>Отмена</Button>
            <Button variant="contained" onClick={savePage}>Сохранить</Button>
          </DialogActions>
        </Dialog>

        <StoragePickerDialog
          open={picker.open}
          imagesOnly
          onClose={() => setPicker({ open: false, for: null, index: -1, target: 'post' })}
          onSelect={onPickDone}
          initialPrefix="cms/"
        />
      </Box>
    </Layout>
  );
};

export default CmsPage;


